import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, ArrowUpRight, Linkedin, Instagram, Facebook, Twitter, Youtube } from "lucide-react";
import { EnquiryForm } from "../components/EnquiryForm";
import { WhatsAppIcon } from "../components/WhatsAppIcon";

const ADDRESS = "4th Floor, North Tower, Astra Towers, ANO - 401, Action Area IIC, Rajarhat, New Town, West Bengal 700161";
const MAPS_URL = "https://share.google/5M1MXBvN6t4fULlQW";

const CARDS = [
  { title: "Office", lines: ["Astra Towers, Rajarhat", "New Town, Kolkata 700161"], href: MAPS_URL, action: "Get Directions", icon: <MapPin size={20} className="text-[#1679B5]" /> },
  { title: "Email", lines: ["enquiry@evontra.co.in"], href: "mailto:enquiry@evontra.co.in", action: "Write to Us", icon: <Mail size={20} className="text-[#1679B5]" /> },
  { title: "Phone", lines: ["+91 98363 72200"], href: "tel:+919836372200", action: "Call Now", icon: <Phone size={20} className="text-[#1679B5]" /> },
  { title: "WhatsApp", lines: ["+91 98363 72200"], href: "https://wa.me/919836372200", action: "Chat Now", icon: <WhatsAppIcon size={20} /> },
];

const SOCIALS = [
  { name: "LinkedIn", icon: Linkedin }, { name: "Instagram", icon: Instagram },
  { name: "Facebook", icon: Facebook }, { name: "Twitter", icon: Twitter }, { name: "YouTube", icon: Youtube },
];

const Contact = () => (
  <main data-testid="contact-page">
    {/* Hero */}
    <section className="ev-grid-bg py-24 relative overflow-hidden">
      <div className="absolute -top-24 right-10 w-[360px] h-[360px] rounded-full bg-[#32B5E8]/15 blur-[100px]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <p className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#1679B5] mb-4">Contact Evontra</p>
        <h1 data-testid="contact-heading" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#081D35] tracking-tight">Let's Get In Touch</h1>
        <div className="mt-7 max-w-3xl space-y-5">
          <p className="text-base md:text-lg text-[#475569] leading-relaxed">
            Evontra is an event management company based in Kolkata, India, offering professional event planning and execution services for businesses and organisations. We work across corporate events, conferences, product launches, employee events, brand activations, live events and more.
          </p>
          <p className="text-base md:text-lg text-[#475569] leading-relaxed">
            From planning and venue coordination to production, technology, entertainment and on-ground execution, our team manages the different aspects of an event under one roof. Whether you are organizing a corporate meeting, conference, annual event or a large-scale experience, Evontra is here to help you plan and deliver it.
          </p>
        </div>
      </div>
    </section>

    {/* Contact cards */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-xl mb-12">
          <p className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#1679B5] mb-3">Reach Us</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#081D35] tracking-tight">Connect With Our Expertise</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CARDS.map((c) => (
            <a data-testid={`contact-card-${c.title.toLowerCase()}`} key={c.title} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="group rounded-2xl border border-slate-200 bg-[#F5F8FA] p-6 hover:bg-white hover:border-[#32B5E8]/40 hover:shadow-[0_20px_45px_-20px_rgba(8,29,53,0.25)] hover:-translate-y-1 transition-all duration-300">
              <span className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-5 group-hover:border-[#32B5E8]/40 transition-colors">{c.icon}</span>
              <p className="text-base font-extrabold text-[#081D35]">{c.title}</p>
              {c.lines.map((l) => <p key={l} className="text-sm text-[#64748B] mt-1">{l}</p>)}
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#1679B5]">
                {c.action} <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>

    {/* Map */}
    <section className="py-20 ev-grid-bg">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-xl mb-10">
          <p className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#1679B5] mb-3">Visit Us</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#081D35] tracking-tight">Find Us on the Map</h2>
          <p className="mt-3 text-sm text-[#64748B]">{ADDRESS}</p>
        </div>
        <div data-testid="office-map" className="rounded-3xl overflow-hidden border border-slate-200 shadow-[0_25px_50px_-25px_rgba(8,29,53,0.3)]">
          <iframe title="Evontra Office — Astra Towers, Rajarhat, New Town" src="https://www.google.com/maps?q=Astra%20Towers%2C%20Action%20Area%20IIC%2C%20Rajarhat%2C%20New%20Town%2C%20West%20Bengal%20700161&output=embed" className="w-full h-[420px] border-0" loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </div>
    </section>

    {/* Enquiry */}
    <section className="py-24 bg-[#081D35] relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#1679B5]/20 blur-[120px]" />
      <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#6FAF2D]/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <p className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#32B5E8] mb-4">Enquiry</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">Let's Create Something Exceptional</h2>
          <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-md">
            Tell us about your event — our team will respond with a tailored concept, format and estimate. Every enquiry goes straight to our planning desk.
          </p>
          <div className="mt-9">
            <p className="text-[11px] font-extrabold tracking-[0.18em] uppercase text-slate-500 mb-4">Follow Evontra</p>
            <div className="flex gap-3">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a data-testid={`social-${s.name.toLowerCase()}`} key={s.name} href="#" aria-label={s.name} className="w-10 h-10 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-slate-300 hover:text-white hover:border-[#32B5E8]/60 hover:bg-white/10 hover:-translate-y-0.5 transition-all duration-200">
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>
          <a data-testid="contact-address-link" href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="mt-10 flex items-start gap-3 text-sm text-slate-400 hover:text-white transition-colors max-w-md">
            <MapPin size={16} className="text-[#32B5E8] mt-0.5 shrink-0" /> {ADDRESS}
          </a>
        </div>
        <div className="rounded-3xl bg-white p-7 sm:p-9 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.5)]">
          <h3 className="text-xl font-extrabold text-[#081D35] mb-6">Plan Your Event</h3>
          <EnquiryForm />
        </div>
      </div>
    </section>
  </main>
);

export default Contact;
